/**
 * Pure screening-evidence resolution used by workforce views.  A provider may
 * keep the underlying credential in another system; that changes the evidence
 * location, never the requirement for a current clearance.
 */
export type ScreeningEvidenceStatus =
  | 'CLEARED'
  | 'CONDITIONAL'
  | 'PENDING'
  | 'REQUIRES_REVIEW'
  | 'EXPIRED'
  | 'REJECTED'
  | 'REVOKED'
  | 'EXCLUDED';

export type ScreeningEvidence = {
  status: ScreeningEvidenceStatus | string;
  validFrom?: string | null;
  validTo?: string | null;
  isActive?: boolean;
  verificationState?: 'VERIFIED' | 'AWAITING_VERIFICATION' | string;
  attestedAt?: string | null;
};

export type ScreeningEvidenceMode = 'HELD_EVIDENCE' | 'EXTERNAL_ATTESTATION';
export type EffectiveScreeningSource = 'held evidence' | 'attested (external system)' | 'none';
export type EffectiveScreeningState = 'cleared' | 'warning' | 'awaiting verification';

export type EffectiveScreeningStatus = {
  state: EffectiveScreeningState;
  source: EffectiveScreeningSource;
  status: string | null;
  validFrom: string | null;
  validTo: string | null;
  warning: boolean;
  hardStop: false;
};

const clearStatuses = new Set(['CLEARED', 'CONDITIONAL']);

function current(row: ScreeningEvidence, asOf: string): boolean {
  return (
    row.isActive !== false &&
    (!row.validFrom || row.validFrom <= asOf) &&
    (!row.validTo || row.validTo >= asOf)
  );
}

function newest<T extends ScreeningEvidence>(rows: readonly T[]): T | undefined {
  return [...rows].sort((left, right) =>
    String(right.attestedAt ?? '').localeCompare(String(left.attestedAt ?? '')),
  )[0];
}

function result(
  state: EffectiveScreeningState,
  source: EffectiveScreeningSource,
  row?: ScreeningEvidence,
): EffectiveScreeningStatus {
  return {
    state,
    source,
    status: row?.status ?? null,
    validFrom: row?.validFrom ?? null,
    validTo: row?.validTo ?? null,
    warning: state !== 'cleared',
    // Attestations change the evidence source only. They must never turn a
    // roster/compliance warning into a novel hard stop.
    hardStop: false,
  };
}

/**
 * Resolves current clearance without inspecting credential identifiers or
 * issuer responses. Awaiting imported/manual attestations remain visible but
 * are not yet cleared, regardless of the preferred evidence mode.
 */
export function effectiveScreeningStatus(
  held: readonly ScreeningEvidence[],
  attestations: readonly ScreeningEvidence[],
  asOf: string,
  mode: ScreeningEvidenceMode,
): EffectiveScreeningStatus {
  const currentHeld = held.filter((row) => current(row, asOf) && clearStatuses.has(row.status));
  const currentAttestations = attestations.filter((row) => current(row, asOf));
  const verifiedAttestation = newest(
    currentAttestations.filter(
      (row) => row.verificationState === 'VERIFIED' && clearStatuses.has(row.status),
    ),
  );
  const awaiting = newest(
    currentAttestations.filter((row) => row.verificationState === 'AWAITING_VERIFICATION'),
  );
  const heldClearance = newest(currentHeld);

  // Directly held evidence is authoritative whenever current, even for a
  // tenant that normally records external attestations.
  if (heldClearance) return result('cleared', 'held evidence', heldClearance);
  if (verifiedAttestation)
    return result('cleared', 'attested (external system)', verifiedAttestation);
  if (awaiting) return result('awaiting verification', 'attested (external system)', awaiting);

  const expiredAttestation = newest(
    attestations.filter((row) => row.validTo != null && row.validTo < asOf),
  );
  const expiredHeld = newest(held.filter((row) => row.validTo != null && row.validTo < asOf));
  // The selected mode is explanatory for a missing record, not a clearance
  // override. Missing and expired evidence remain the existing warning state.
  return result(
    'warning',
    mode === 'EXTERNAL_ATTESTATION' && expiredAttestation
      ? 'attested (external system)'
      : expiredHeld
        ? 'held evidence'
        : 'none',
    expiredAttestation ?? expiredHeld,
  );
}
